// Pure display helpers and constants for the map editor's tool/picker UI.
// Extracted from MapEditor.vue so the bar components and the editor share one
// source of truth. No reactivity here — just enum → label/colour/icon mappings.
//
// §6 maintenance contract: when a new ObjectiveType/TerrainType/etc. is added to
// @scrolldex/shared, extend the maps/switches below so it appears in the editor.
import {
  type IMapZone,
  ObjectiveType,
  TerrainType,
  TerrainSize,
  ZoneType,
  Mirror,
} from '@scrolldex/shared';

export type ToolKind = 'Select' | 'Objective' | 'Terrain' | 'Zone' | 'Label' | 'Arrow' | 'Icon';

export const TOOLS: { key: ToolKind; label: string; icon: string }[] = [
  { key: 'Select', label: 'Select', icon: 'arrow-pointer' },
  { key: 'Zone', label: 'Zone', icon: 'vector-square' },
  { key: 'Objective', label: 'Objective', icon: 'bullseye' },
  { key: 'Terrain', label: 'Terrain', icon: 'tree' },
  { key: 'Label', label: 'Label', icon: 'tag' },
  { key: 'Arrow', label: 'Arrow', icon: 'arrow-right-long' },
  { key: 'Icon', label: 'Icon', icon: 'shapes' },
];

// Icons the picker offers. The stored value (IMapIcon.icon) is freeform, so this
// is just the curated starting set — the 3 terrain glyphs plus a solid circle and
// biohazard. Adding more means registering the glyph (main.ts + mapRender) and
// appending here. `label` is only for the picker tooltip; the on-map caption comes
// from the icon's own `legend`.
export const ICON_OPTIONS: { name: string; label: string }[] = [
  { name: 'house-chimney-crack', label: 'House' },
  { name: 'tree', label: 'Tree' },
  { name: 'wand-sparkles', label: 'Wand' },
  { name: 'circle', label: 'Circle' },
  { name: 'biohazard', label: 'Biohazard' },
];

// Mirror picker (objective + terrain): None / left-right (X) / top-bottom (Y) / both.
export const MIRROR_OPTIONS: { val: Mirror | 'none'; icon: string; name: string }[] = [
  { val: 'none', icon: 'ban', name: 'No mirror' },
  { val: Mirror.X, icon: 'arrows-left-right', name: 'Mirror left–right' },
  { val: Mirror.Y, icon: 'arrows-up-down', name: 'Mirror top–bottom' },
  { val: Mirror.Both, icon: 'up-down-left-right', name: 'Mirror both' },
];

export function mirrorName(m: Mirror | 'none'): string {
  return MIRROR_OPTIONS.find((o) => o.val === m)?.name ?? 'No mirror';
}

// Objective picker swatch colors (mirror the board's .objective-* CSS so the
// picker reads the same as what gets placed).
export const OBJ_SWATCH: Record<ObjectiveType, { bg: string; fg: string; border: string }> = {
  [ObjectiveType.Regular]: { bg: '#d7b21d', fg: '#3a2f00', border: '#a8860f' },
  [ObjectiveType.Primary]: { bg: '#ffd700', fg: '#5c4a00', border: '#b8960f' },
  [ObjectiveType.Secondary]: { bg: '#c0c0c0', fg: '#333333', border: '#888888' },
  [ObjectiveType.Oakenbrow]: { bg: '#dc2626', fg: '#ffffff', border: '#991b1b' },
  [ObjectiveType.Gnarlroot]: { bg: '#16a34a', fg: '#ffffff', border: '#15803d' },
  [ObjectiveType.Heartwood]: { bg: '#2563eb', fg: '#ffffff', border: '#1d4ed8' },
  [ObjectiveType.Winterleaf]: { bg: '#7c3aed', fg: '#ffffff', border: '#6d28d9' },
  [ObjectiveType.GoldenLions]: { bg: '#dc2626', fg: '#ffffff', border: '#991b1b' },
  [ObjectiveType.SunSeekers]: { bg: '#d7b21d', fg: '#3a2f00', border: '#a8860f' },
  [ObjectiveType.Heldenhain]: { bg: '#171717', fg: '#ffffff', border: '#000000' },
  [ObjectiveType.Sigmarite]: { bg: '#2563eb', fg: '#ffffff', border: '#1d4ed8' },
};

export function swatchStyle(t: ObjectiveType) {
  const s = OBJ_SWATCH[t];
  return { background: s.bg, color: s.fg, borderColor: s.border };
}

export function objectiveTypeName(t: ObjectiveType): string {
  switch (t) {
    case ObjectiveType.Regular:
      return 'Regular';
    case ObjectiveType.Primary:
      return 'Primary';
    case ObjectiveType.Secondary:
      return 'Secondary';
    case ObjectiveType.Oakenbrow:
      return 'Oakenbrow';
    case ObjectiveType.Gnarlroot:
      return 'Gnarlroot';
    case ObjectiveType.Heartwood:
      return 'Heartwood';
    case ObjectiveType.Winterleaf:
      return 'Winterleaf';
    case ObjectiveType.GoldenLions:
      return 'Golden Lions';
    case ObjectiveType.SunSeekers:
      return 'Sun Seekers';
    case ObjectiveType.Heldenhain:
      return 'Heldenhain';
    case ObjectiveType.Sigmarite:
      return 'Sigmarite';
  }
}

// Single/double-letter glyph drawn inside the objective marker on the board.
export function objectiveDisplayLabel(type: ObjectiveType): string {
  switch (type) {
    case ObjectiveType.Regular:
      return 'O';
    case ObjectiveType.Primary:
      return 'P';
    case ObjectiveType.Secondary:
      return 'S';
    case ObjectiveType.Oakenbrow:
      return 'O';
    case ObjectiveType.Gnarlroot:
      return 'G';
    case ObjectiveType.Heartwood:
      return 'H';
    case ObjectiveType.Winterleaf:
      return 'W';
    case ObjectiveType.GoldenLions:
      return 'GL';
    case ObjectiveType.SunSeekers:
      return 'SS';
    case ObjectiveType.Heldenhain:
      return 'H';
    case ObjectiveType.Sigmarite:
      return 'S';
  }
}

// Font Awesome icon name for objectives shown as a glyph in the picker instead
// of a letter (null → use the objectiveDisplayLabel letter). Mirrors the board's
// objectiveIconDef in mapRender.ts.
export function objectiveIconName(t: ObjectiveType): string | null {
  switch (t) {
    case ObjectiveType.Regular:
      return 'star';
    default:
      return null;
  }
}

export function zoneBtnLabel(t: ZoneType): string {
  switch (t) {
    case ZoneType.Attacker:
      return 'Attacker';
    case ZoneType.Defender:
      return 'Defender';
    case ZoneType.Other:
      return 'Other';
  }
}

// Board zone label. A custom `label` overrides the type default; otherwise
// Attacker/Defender render their name and Other renders nothing.
export function zoneDisplayLabel(zone: IMapZone): string {
  const custom = zone.label?.trim();
  if (custom) return custom;
  switch (zone.type) {
    case ZoneType.Attacker:
      return 'Attacker';
    case ZoneType.Defender:
      return 'Defender';
    case ZoneType.Other:
      return '';
  }
}

// Split a zone label into rendered lines (custom labels may contain newlines).
export function zoneDisplayLabelLines(zone: IMapZone): string[] {
  return zoneDisplayLabel(zone).split('\n');
}

// Vertical offset (in em) for each label line so the block stays centered.
export function zoneLabelLineDy(count: number, index: number): string {
  return index === 0 ? `${(-(count - 1) * 0.55).toFixed(2)}em` : '1.1em';
}

export function terrainIconName(t: TerrainType): string {
  switch (t) {
    case TerrainType.Ruin:
      return 'house-chimney-crack';
    case TerrainType.Obscuring:
      return 'tree';
    case TerrainType.PlaceOfPower:
      return 'wand-sparkles';
  }
}

export function terrainTypeName(t: TerrainType): string {
  switch (t) {
    case TerrainType.Ruin:
      return 'Ruin';
    case TerrainType.Obscuring:
      return 'Obscuring';
    case TerrainType.PlaceOfPower:
      return 'Place of Power';
  }
}

// Tool-visualization sizes for the terrain size picker (px, not to scale).
export function sizeDotPx(sz: TerrainSize): number {
  switch (sz) {
    case TerrainSize.Small:
      return 12;
    case TerrainSize.Medium:
      return 18;
    case TerrainSize.Large:
      return 24;
  }
}

export function sizeName(sz: TerrainSize): string {
  switch (sz) {
    case TerrainSize.Small:
      return 'Small';
    case TerrainSize.Medium:
      return 'Medium';
    case TerrainSize.Large:
      return 'Large';
  }
}
